import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";

import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

const QuizPage = ({ testno, onQuizComplete, onOpenAudio }) => {
  const [current, setCurrent] = useState(0);

  const [selected, setSelected] = useState("");

  const [submitted, setSubmitted] = useState(false);

  const [time, setTime] = useState(0);

  const [userAnswers, setUserAnswers] = useState([]);

  const [questions, setQuestions] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const timerRef = useRef(null);

  const navigate = useNavigate();

  // =========================
  // CHECK LOGIN
  // =========================

  useEffect(() => {
    const loggedIn = localStorage.getItem("login");

    if (loggedIn !== "true") {
      navigate("/");
    }
  }, [navigate]);

  // =========================
  // FETCH QUESTIONS
  // =========================

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!testno) {
        setError(
          "Test number is missing. Please go back and select a topic."
        );

        setLoading(false);

        return;
      }

      setLoading(true);

      setError(null);

      try {
        const q = query(
          collection(db, "mocktest"),
          where("testid", "==", testno),
          orderBy("qno")
        );

        const querySnapshot = await getDocs(q);

        const fetchedQuestions = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();

          fetchedQuestions.push({
            // Question number
            id: data.qno,

            // Question text
            question: data.q,

            // Question image
            qimg: data.qimg || "",

            // Options
            options: {
              a: data.a,
              b: data.b,
              c: data.c,
              d: data.d,
            },

            // Option images
            imgop1: data.imgop1 || "",
            imgop2: data.imgop2 || "",
            imgop3: data.imgop3 || "",
            imgop4: data.imgop4 || "",

            // Correct option
            correctop: data.ans,

            // Explanation audio
            expaudio: data.ansaudio || "",

            // Maths solution image
            solutionimg: data.solutionimg || "",

            // Test ID
            testid: data.testid || testno,
          });
        });

        if (fetchedQuestions.length === 0) {
          setError(`No questions found for test: ${testno}`);
        } else {
          setQuestions(fetchedQuestions);
        }
      } catch (err) {
        console.error("Error fetching questions:", err);

        setError(
          "Failed to load questions. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [testno]);

  // =========================
  // TIMER
  // =========================

  useEffect(() => {
    setTime(0);

    setSelected("");

    setSubmitted(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTime((t) => t + 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [current]);

  // =========================
  // CURRENT QUESTION
  // =========================

  const question = questions[current];

  // =========================
  // FORMAT TIME
  // =========================

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);

    const remaining = seconds % 60;

    return minutes > 0
      ? `${minutes}m ${remaining}s`
      : `${remaining}s`;
  };

  // =========================
  // SUBMIT ANSWER
  // =========================

  const handleSubmit = () => {
    if (!selected || !question) {
      return;
    }

    clearInterval(timerRef.current);

    const correctAnswerText =
      question.options[question.correctop];

    const isCorrect = selected === correctAnswerText;

    const newAnswer = {
      questionId: question.id,

      question: question.question,

      selected: selected,

      correct: correctAnswerText,

      timeTaken: time,

      isCorrect: isCorrect,

      // Explanation audio
      expaudio: question.expaudio,

      // Maths solution image
      solutionimg: question.solutionimg,

      // Test ID
      testid: question.testid,
    };

    setUserAnswers([...userAnswers, newAnswer]);

    setSubmitted(true);
  };

  // =========================
  // NEXT QUESTION
  // =========================

  const handleNext = () => {
    if (current === questions.length - 1) {
      onQuizComplete(userAnswers);
    } else {
      setCurrent(current + 1);
    }
  };

  // =========================
  // OPTION STYLE
  // =========================

  const getOptionClass = (optValue) => {
    if (!question) {
      return "option";
    }

    const correctAnswerText =
      question.options[question.correctop];

    if (!submitted) {
      return selected === optValue
        ? "option selected"
        : "option";
    }

    if (optValue === correctAnswerText) {
      return "option correct";
    }

    if (optValue === selected) {
      return "option wrong";
    }

    return "option";
  };

  // =========================
  // LOADING
  // =========================

  if (loading) {
    return (
      <div
        className="container"
        style={{
          textAlign: "center",
          padding: "100px 20px",
        }}
      >
        <p>
          Loading questions for test {testno || "..."}...
        </p>
      </div>
    );
  }

  // =========================
  // ERROR
  // =========================

  if (error) {
    return (
      <div
        className="container"
        style={{
          textAlign: "center",
          padding: "80px 20px",
          color: "#ef4444",
        }}
      >
        <p>{error}</p>

        <button
          onClick={() => window.history.back()}
          style={{
            marginTop: "20px",
            padding: "12px 24px",
          }}
        >
          ← Go Back
        </button>
      </div>
    );
  }

  // =========================
  // NO QUESTIONS
  // =========================

  if (!questions.length || !question) {
    return (
      <div
        className="container"
        style={{
          textAlign: "center",
          padding: "80px 20px",
        }}
      >
        <p>No questions found for this test.</p>
      </div>
    );
  }

  // =========================
  // OPTION IMAGES
  // =========================

  const optionImages = {
    a: question.imgop1,
    b: question.imgop2,
    c: question.imgop3,
    d: question.imgop4,
  };

  // =========================
  // CHECK IF MATHS TEST
  // =========================

  const isMaths = question.testid
    .toLowerCase()
    .startsWith("m");

  return (
    <div className="container">

      {/* Progress */}

      <div className="progress-badge">
        Question <strong>{current + 1}</strong> of{" "}
        {questions.length}
      </div>

      {/* Timer */}

      <div className="timer">
        ⏱ {formatTime(time)}
      </div>

      {/* Question Text */}

      <h2 className="question-text">
        {question.question}
      </h2>

      {/* Question Image */}

      {question.qimg && (
        <div
          style={{
            textAlign: "center",
            margin: "20px 0",
          }}
        >
          <img
            src={question.qimg}
            alt="Question Illustration"
            style={{
              maxWidth: "100%",
              maxHeight: "320px",
              borderRadius: "12px",
              boxShadow:
                "0 4px 12px rgba(0,0,0,0.1)",
              objectFit: "contain",
            }}
          />
        </div>
      )}

      {/* OPTIONS */}

      <div className="options">
        {Object.entries(question.options).map(
          ([key, value]) => {
            const imgUrl = optionImages[key];

            return (
              <label
                key={key}
                className={getOptionClass(value)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <input
                  type="radio"
                  checked={selected === value}
                  onChange={() => {
                    if (!submitted) {
                      setSelected(value);
                    }
                  }}
                  disabled={submitted}
                />

                <div
                  style={{
                    flex: 1,
                  }}
                >
                  <strong>
                    {key.toUpperCase()}.
                  </strong>{" "}
                  {value}
                </div>

                {/* Option Image */}

                {imgUrl && (
                  <img
                    src={imgUrl}
                    alt={`Option ${key.toUpperCase()}`}
                    style={{
                      maxWidth: "90px",
                      maxHeight: "90px",
                      borderRadius: "8px",
                      objectFit: "contain",
                      border:
                        "1px solid #e5e7eb",
                    }}
                  />
                )}
              </label>
            );
          }
        )}
      </div>

      {/* SUBMIT BUTTON */}

      {!submitted && (
        <button
          className="btn submit"
          onClick={handleSubmit}
          disabled={!selected}
        >
          Submit Answer
        </button>
      )}

      {/* AFTER SUBMISSION */}

      {submitted && (
        <>
          {/* EXPLANATION AND SOLUTION BUTTONS */}

          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginTop: "20px",
            }}
          >

            {/* LISTEN EXPLANATION */}

            <button
              className="btn audio"
              onClick={() => {
                onOpenAudio({
                  type: "audio",
                  url: question.expaudio,
                });
              }}
              disabled={!question.expaudio}
            >
              🔊 Listen Explanation
            </button>

            {/* VIEW SOLUTION - ONLY FOR MATHS */}

            {isMaths && (
              <button
                className="btn audio"
                onClick={() => {
                  onOpenAudio({
                    type: "image",
                    url: question.solutionimg,
                  });
                }}
                disabled={!question.solutionimg}
              >
                🧮 View Solution
              </button>
            )}
          </div>

          {/* NEXT BUTTON */}

          <button
            className="btn next"
            onClick={handleNext}
          >
            {current === questions.length - 1
              ? "Finish Quiz"
              : "Next Question →"}
          </button>
        </>
      )}
    </div>
  );
};

export default QuizPage;